import React, { useState } from "react";
import { NavLink, useParams } from "react-router-dom";
import { FaMapMarkerAlt, FaRegClock, FaRegCalendarAlt } from "react-icons/fa";
import { FiEdit2, FiTrash2, FiX } from "react-icons/fi";
import {
  useDeleteSocietyTimelineMutation,
  useGetSocietyTimelineQuery,
} from "../../redux/societyApi";
import AddUpdateTimelineEntry from "./AddUpdateTimelineEntry";
import { toast } from "react-toastify"; // adjust import to whatever toast lib you use
import { IoDocumentText } from "react-icons/io5";
import { handleDownload } from "../../utils/Utils";

const getBgColor = (status) => {
  if (status === "approved") return "bg-[#DEE9E4]";
  if (status === "unapproved" || status === "underProcess")
    return "bg-[#DDEBF7]";
  return "bg-[#F1D1CE]";
};
const gettextColor = (status) => {
  if (status === "approved") return "text-[#00643B]";
  if (status === "unapproved" || status === "underProcess")
    return "text-[#008CFF]";
  return "text-[#D80000]";
};

const formatStatusLabel = (status) => {
  if (!status) return "Unknown";
  if (status === "approved") return "Approved";
  if (status === "unapproved") return "Unapproved";
  if (status === "underProcess") return "Under Process";
  if (status === "illegal") return "Illegal";
  return status;
};

const formatDate = (dateStr) =>
  new Date(dateStr).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const formatTime = (dateStr) =>
  new Date(dateStr).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });

const formatFileSize = (bytes) => {
  if (!bytes) return "";
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(0)}KB`;
  return `${(kb / 1024).toFixed(1)}MB`;
};

const TimelineSkeletonItem = ({ isLast }) => (
  <div className="relative flex gap-4 pb-6">
    <div className="flex flex-col items-center">
      <div className="z-10 w-8 h-8 rounded-full bg-gray-200 animate-pulse shrink-0" />
      {!isLast && <div className="w-[2px] flex-1 bg-gray-200 mt-1" />}
    </div>
    <div className="flex-1 bg-[#F7F8FA] rounded-xl p-4 flex flex-col md:flex-row gap-4 animate-pulse">
      <div className="md:w-[180px] shrink-0 flex flex-col gap-2">
        <div className="h-3 w-20 bg-gray-200 rounded" />
        <div className="h-10 bg-gray-200 rounded-lg" />
        <div className="h-10 bg-gray-200 rounded-lg" />
      </div>
      <div className="flex-1 flex flex-col gap-2">
        <div className="flex items-center justify-between gap-3">
          <div className="h-5 w-24 bg-gray-200 rounded-md" />
          <div className="h-3 w-32 bg-gray-200 rounded" />
        </div>
        <div className="h-4 w-1/2 bg-gray-200 rounded mt-1" />
        <div className="h-3 w-full bg-gray-200 rounded" />
        <div className="h-3 w-3/4 bg-gray-200 rounded" />
      </div>
    </div>
  </div>
);

const SocietyTimeline = () => {
  const { id } = useParams();
  const isId = Boolean(id);

  const [open, setOpen] = useState(false);
  const [selectedTimeline, setSelectedTimeline] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const { data, isLoading } = useGetSocietyTimelineQuery(
    { societyId: id },
    {
      refetchOnFocus: false,
      skip: !isId,
    },
  );
  const [viewFile, setViewFile] = useState(null);

  const [deleteSocietyTimeline, { isLoading: isDeleting }] =
    useDeleteSocietyTimelineMutation();

  const society = data?.society;
  const timelines = data?.timelines || [];

  const handleAddClick = () => {
    setSelectedTimeline(null);
    setOpen(true);
  };

  const handleEditClick = (item) => {
    setSelectedTimeline(item);
    setOpen(true);
  };

  const handleDeleteClick = (item) => {
    setDeleteId(item._id);
  };

  const confirmDelete = async () => {
    try {
      await deleteSocietyTimeline(deleteId).unwrap();
      toast.success("Timeline entry deleted successfully");
    } catch (err) {
      toast.error(err?.data?.message || "Failed to delete timeline entry");
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <div className="flex flex-col gap-3 bg-white rounded-xl xl:p-8 lg:p-6 p-4 ">
      <h1 className="text-[#05004E] lg:text-[1.8rem] md:text-[1.4rem] text-[1.2rem] font-semibold">
        Society Timeline
      </h1>

      <div className="flex justify-between items-start flex-wrap gap-3">
        <div className="flex flex-col gap-1">
          {isLoading ? (
            <>
              <div className="h-5 w-40 bg-gray-200 rounded animate-pulse" />
              <div className="h-3 w-28 bg-gray-200 rounded animate-pulse mt-1" />
            </>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <h2 className="text-[#05004E] lg:text-[1.2rem] text-[1rem] font-bold">
                  {society?.name}
                </h2>
                {society?.status && (
                  <span
                    className={`flex items-center gap-1 text-[.75rem] font-medium px-3 py-1 rounded-full ${getBgColor(
                      society.status,
                    )} ${gettextColor(society.status)}`}
                  >
                    {formatStatusLabel(society.status)}
                  </span>
                )}
              </div>
              {society?.location && (
                <p className="flex items-center gap-1.5 text-gray-500 text-[.85rem]">
                  <FaMapMarkerAlt className="text-gray-400" />
                  {society.location}
                </p>
              )}
            </>
          )}
        </div>

        <button
          onClick={handleAddClick}
          className="md:w-[10rem] w-max  bg-[#00643b] text-center lg:py-3 md:py-2 md:px-0 px-2 py-1.5 lg:text-[1rem] md:text-[.9rem] text-[.75rem] md:rounded-xl rounded-lg font-medium text-white"
        >
          + Add Timeline
        </button>
      </div>

      {/* Timeline list */}
      <div className="relative mt-4">
        {isLoading ? (
          <>
            <TimelineSkeletonItem />
            <TimelineSkeletonItem />
            <TimelineSkeletonItem isLast />
          </>
        ) : (
          <>
            {timelines.map((item, index) => {
              const isLast = index === timelines.length - 1;
              return (
                <div key={item._id} className="relative flex gap-4 pb-6">
                  {/* Numbered circle + connecting line */}
                  <div className="flex flex-col items-center">
                    <div className="z-10 flex items-center justify-center w-8 h-8 rounded-full bg-[#00643B] text-white text-sm font-semibold shrink-0">
                      {index + 1}
                    </div>
                    {!isLast && (
                      <div className="w-[2px] flex-1 bg-[#00643B]/30 mt-1" />
                    )}
                  </div>

                  {/* Card */}
                  <div className="flex-1 bg-[#F7F8FA] rounded-xl p-4 flex flex-col items-stretch md:flex-row gap-4 relative">
                    {/* Attachments */}
                    {item.docs?.length > 0 && (
                      <div className="md:w-[180px] shrink-0">
                        <p className="text-gray-400 text-[.7rem] font-medium uppercase mb-2">
                          Attachments
                        </p>
                        <div className="flex flex-col gap-2">
                          {item.docs.map((doc) =>
                            doc?.fileType === "application/pdf" ||
                            doc?.fileType?.startsWith("image/") ? (
                              <NavLink
                                key={doc._id}
                                onClick={() =>
                                  setViewFile({
                                    url: doc?.docUrl,
                                    mimeType: doc?.fileType,
                                  })
                                }
                                className="flex items-center gap-2 bg-white rounded-lg px-2 py-1.5 hover:bg-gray-50"
                              >
                                <IoDocumentText className="text-gray-400 text-[1.1rem] shrink-0" />
                                <div className="flex flex-col overflow-hidden">
                                  <span className="text-[.75rem] text-gray-700 truncate max-w-[110px]">
                                    {doc.fileName}
                                  </span>
                                  <span className="text-[.65rem] text-gray-400">
                                    {formatFileSize(doc.fileSize)}
                                  </span>
                                </div>
                              </NavLink>
                            ) : (
                              <NavLink
                                key={doc._id}
                                onClick={() =>
                                  handleDownload(
                                    doc?.docUrl,
                                    `${doc?.fileName || "file"}`,
                                    doc?.fileType,
                                  )
                                }
                                className="flex items-center gap-2 bg-white rounded-lg px-2 py-1.5 hover:bg-gray-50"
                              >
                                <IoDocumentText className="text-gray-400 text-[1.1rem] shrink-0" />
                                <div className="flex flex-col overflow-hidden">
                                  <span className="text-[.75rem] text-gray-700 truncate max-w-[110px]">
                                    {doc.fileName}
                                  </span>
                                  <span className="text-[.65rem] text-gray-400">
                                    {formatFileSize(doc.fileSize)}
                                  </span>
                                </div>
                              </NavLink>
                            ),
                          )}
                        </div>
                      </div>
                    )}

                    {/* Content */}
                    <div className="flex-1 flex flex-col gap-1 h-full">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <span className="bg-[#DDEBF7] text-[#008CFF] text-[.7rem] font-semibold px-2 py-0.5 rounded-md">
                          {item.applicationNo}
                        </span>
                        <div className="flex items-center gap-3 text-[.75rem] text-gray-400">
                          <span className="flex items-center gap-1">
                            <FaRegCalendarAlt />
                            {formatDate(item.createdAt)}
                          </span>
                          <span className="flex items-center gap-1">
                            <FaRegClock />
                            {formatTime(item.createdAt)}
                          </span>
                        </div>
                      </div>

                      <h3 className="text-[#05004E] font-semibold text-[1rem] mt-1">
                        {item.title}
                      </h3>
                      <p className="text-gray-500 text-[.85rem] leading-relaxed">
                        {item.description}
                      </p>
                      {/* Edit / Delete actions */}
                      <div className=" flex items-end justify-end gap-2 h-full">
                        <button
                          onClick={() => handleEditClick(item)}
                          title="Edit"
                          className="flex items-center justify-center w-7 h-7 rounded-md bg-white text-[#008CFF] hover:bg-[#DDEBF7] transition"
                        >
                          <FiEdit2 size={14} />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(item)}
                          title="Delete"
                          className="flex items-center justify-center w-7 h-7 rounded-md bg-white text-[#D80000] hover:bg-[#F1D1CE] transition"
                        >
                          <FiTrash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {timelines.length === 0 && (
              <p className="text-center text-gray-400 py-10">
                No timeline entries yet.
              </p>
            )}
          </>
        )}
      </div>

      <AddUpdateTimelineEntry
        open={open}
        setOpen={setOpen}
        societyId={id}
        isNew={!selectedTimeline}
        oldData={selectedTimeline}
        id={selectedTimeline?._id}
        setSelectedTimeline={setSelectedTimeline}
      />

      {/* Delete confirmation modal */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-[90%] max-w-sm flex flex-col gap-4">
            <h3 className="text-[#05004E] font-semibold text-[1.1rem]">
              Delete Timeline Entry
            </h3>
            <p className="text-gray-500 text-[.9rem]">
              Are you sure you want to delete this timeline entry? This action
              cannot be undone.
            </p>
            <div className="flex justify-end gap-3 mt-2">
              <button
                onClick={() => setDeleteId(null)}
                disabled={isDeleting}
                className="px-4 py-2 rounded-lg text-[.85rem] font-medium text-gray-600 bg-gray-100 hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={isDeleting}
                className="px-4 py-2 rounded-lg text-[.85rem] font-medium text-white bg-[#D80000] hover:bg-[#b00000] disabled:opacity-60"
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {viewFile && (
        <div className="fixed inset-0 bg-black bg-opacity-10 backdrop-blur-sm flex items-center justify-center z-[200]">
          <div className="relative bg-white/80 border border-white/30 backdrop-blur-lg rounded-2xl shadow-xl p-4  w-[90%] h-[95vh]  animate-fade-in text-black">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setViewFile(null);
              }}
              className="absolute top-4 right-4 text-[#00643b]"
              aria-label="Close modal"
            >
              <FiX className="size-6" />
            </button>
            <h2 className="text-[1.5rem] font-bold lg:mb-4 mb-3 text-[#00643b]">
              Society Timeline file
            </h2>
            {viewFile.mimeType === "application/pdf" ? (
              <iframe
                src={viewFile.url}
                width="100%"
                height="90%"
                className="rounded-[1rem] mt-2"
                title="PDF Preview"
              />
            ) : viewFile.mimeType.startsWith("image/") ? (
              <img
                src={viewFile.url}
                alt="file Preview"
                className="rounded-[1rem] mt-2 w-full h-[90%]  object-contain"
              />
            ) : (
              <p className="text-red-500 mt-4  text-center">
                This file type cannot be previewed.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SocietyTimeline;
